<script setup lang="ts">
import { FileWithBlob, useFilesStore } from "@/stores/files";
import prettyBytes from "pretty-bytes";
import { computed } from "vue";

const fileStore = useFilesStore();
const props = defineProps<{ file: FileWithBlob }>();

const uploadingProgress = computed(() => {
    return (props.file.uploadedSize / props.file.fileSize) * 100;
});

const storeFileCreatedAt = computed(() => {
    const file = props.file;
    if (!file.isDuplicate) return "";
    if (!file.storeFileCreatedAt) return "";
    return new Date(file.storeFileCreatedAt).toJSON().slice(0, 10);
});
</script>

<template>
    <div class="file">
        <span class="file__name" title="File name"> {{ file.fileName }}</span>
        <span class="file__size">
            {{ prettyBytes(file.fileSize) }}
            <span v-if="!file.isUploaded && !file.isDuplicate">({{ uploadingProgress.toFixed(2) }})%</span>
        </span>
        <span class="file__mark" v-if="file.isDuplicate" data-warn>Duplicate</span>
        <span class="file__mark" v-else-if="file.hasError" data-error>Error</span>
        <span class="file__mark" v-else-if="file.isUploaded" data-ok>Uploaded</span>
        <span class="file__mark" v-else-if="!file.attachedFile" data-error>Detached</span>
        <span class="file__mark" v-else data-ok>Pending</span>

        <span class="file__actions">
            <div @click="fileStore.deleteSessionFile(file)">x</div>
            <div :title="file.hashSha256" class="file__hash">HS</div>
        </span>
        <div class="file__status">
            <span v-if="file.isDuplicate">
                duplicate to {{ file.databaseId }} uploaded on {{ storeFileCreatedAt }} |
                {{ file.storeFileIsOpensource ? "open source" : "closed source" }}
            </span>
        </div>
    </div>
</template>
<style scoped lang="scss">
.file {
    display: grid;
    grid-template-columns: auto 100px 100px min-content;

    border-bottom: 1px solid #727272;

    align-items: center;

    position: relative;

    &__name {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        flex-grow: 1;
        font-size: 14px;
        font-family: "Trebuchet MS";
        padding: 12px;
    }
    &__mark {
        font-size: 12px;
        text-align: center;
        text-transform: uppercase;
    }
    &__size {
        font-size: 12px;
        text-align: center;
        text-wrap: nowrap;
    }

    [data-error] {
        color: #dd300e;
    }
    [data-warn] {
        color: #de780b;
    }
    [data-ok] {
        color: #389b0a;
    }

    &__status {
        display: flex;
        gap: 10px;
        align-items: center;
        position: absolute;
        bottom: 4px;
        left: 10px;
        font-size: 10px;
        color: #727272;
        font-style: italic;
    }

    &__actions {
        border-left: 1px solid #727272;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        div {
            height: 25px;
            width: 25px;
            display: flex;
            justify-content: center;
            align-items: center;
            user-select: none;
            color: #727272;

            &:first-child {
                border-bottom: 1px solid #a1a1a1;
                background-color: #a1a1a1;
                cursor: pointer;
            }
        }
    }
}
</style>
